import { ALLOWED_DOCUMENT_MIME_TYPES, MAX_DOCUMENT_BYTES, MIME_BY_EXTENSION } from './constants';

/** Sous-ensemble de `File` dont on a besoin (testable sans DOM). */
export interface FileLike {
  name: string;
  size: number;
  type: string;
}

const ALLOWED = new Set<string>(ALLOWED_DOCUMENT_MIME_TYPES);

export function fileExtension(name: string): string {
  const dot = name.lastIndexOf('.');
  if (dot <= 0 || dot === name.length - 1) return '';
  return name.slice(dot + 1).toLowerCase();
}

/**
 * Type envoyé à Storage. On fait confiance à `file.type` quand il est dans la
 * liste fermée ; sinon (vide, `application/octet-stream`, alias non standard
 * comme `image/jpg`) on retombe sur l'extension. `null` = refusé.
 */
export function resolveMimeType(file: FileLike): string | null {
  const declared = (file.type ?? '').split(';')[0]?.trim().toLowerCase() ?? '';
  if (declared && ALLOWED.has(declared)) return declared;
  return MIME_BY_EXTENSION[fileExtension(file.name)] ?? null;
}

/** Message français affichable, ou `null` si le fichier est acceptable. */
export function validateDocumentFile(file: FileLike): string | null {
  if (file.size <= 0) return 'Ce fichier est vide.';
  if (file.size > MAX_DOCUMENT_BYTES) {
    return `Ce fichier est trop volumineux (maximum ${MAX_DOCUMENT_BYTES / (1024 * 1024)} Mo).`;
  }
  if (resolveMimeType(file) === null) {
    return "Ce type de fichier n'est pas accepté. Formats acceptés : images, PDF, Word, Excel, PowerPoint, texte.";
  }
  return null;
}

/**
 * Nom sûr pour un chemin Storage : sans accents, sans `/`, `%`, espaces ni
 * caractère de contrôle, extension conservée en minuscules, jamais vide.
 */
export function safeFileName(name: string): string {
  const normalized = name.normalize('NFD').replace(/[̀-ͯ]/g, '');
  const dot = normalized.lastIndexOf('.');
  const rawBase = dot > 0 ? normalized.slice(0, dot) : normalized;
  const rawExt = dot > 0 ? normalized.slice(dot + 1) : '';
  const clean = (s: string) =>
    s
      .replace(/[^A-Za-z0-9._-]+/g, '_')
      .replace(/_+/g, '_')
      .replace(/^[._-]+|[._-]+$/g, '');
  const base = clean(rawBase).slice(0, 80) || 'fichier';
  const ext = clean(rawExt).slice(0, 10).toLowerCase();
  return ext ? `${base}.${ext}` : base;
}

/** `{uploaded_by}/{doc_id}/{nom}` — le préfixe est imposé par un CHECK SQL (0261). */
export function buildStoragePath(userId: string, docId: string, fileName: string): string {
  return `${userId}/${docId}/${safeFileName(fileName)}`;
}

/** « 1,5 Ko », « 2 Mo » — format français. */
export function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes < 1024) return `${bytes} o`;
  const units = ['Ko', 'Mo', 'Go'];
  let value = bytes / 1024;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i += 1;
  }
  const rounded = value >= 10 ? Math.round(value) : Math.round(value * 10) / 10;
  return `${String(rounded).replace('.', ',')} ${units[i]}`;
}

export type DocumentKind = 'image' | 'pdf' | 'word' | 'sheet' | 'slides' | 'text' | 'other';

export function documentKind(mimeType: string): DocumentKind {
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType === 'application/pdf') return 'pdf';
  if (
    mimeType.includes('wordprocessingml') ||
    mimeType === 'application/msword' ||
    mimeType.endsWith('opendocument.text')
  ) {
    return 'word';
  }
  if (
    mimeType.includes('spreadsheetml') ||
    mimeType === 'application/vnd.ms-excel' ||
    mimeType.endsWith('opendocument.spreadsheet') ||
    mimeType === 'text/csv'
  ) {
    return 'sheet';
  }
  if (mimeType.includes('presentationml') || mimeType === 'application/vnd.ms-powerpoint')
    return 'slides';
  if (mimeType === 'text/plain') return 'text';
  return 'other';
}

export function isImageMime(mimeType: string): boolean {
  return mimeType.startsWith('image/');
}

/** UUID v4 — `crypto.randomUUID` quand il existe, sinon repli `getRandomValues`. */
export function newDocumentId(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  const bytes = new Uint8Array(16);
  if (c && typeof c.getRandomValues === 'function') c.getRandomValues(bytes);
  else for (let i = 0; i < 16; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0'));
  return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex.slice(6, 8).join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10).join('')}`;
}
